import { ErrorBoundary } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useMemo, useState } from 'react';

import { useInsightFiltersFromSearchParams } from '../hook/use-insight-filters-from-search-params';
import { FilteredPostsHeader } from './filtered-posts.header';

import { SocialPostsGrid } from '@/features/(insight)/posts/ui/social-post-card/social-posts-grid';
import { SocialPostsGridHeader } from '@/features/(insight)/posts/ui/social-posts-grid-header';
import { useInfinitePosts } from '@/features/(insight)/shared/hooks/use-infinite-posts';
import {
  BoardsSelectionActionBar,
  requestSaveToBoardsDialog,
  useBoardsPosts,
} from '@/features/boards';
import { ErrorFallback } from '@/shared/components/error-fallback';
import { PageLoader } from '@/shared/components/loader/page-loader';
import { useFilteredPageDateRangeValue } from '@/shared/hooks/use-filtered-page-date-range';
import { useCurrentPageRoutingStatus } from '@/shared/hooks/use-is-routing';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import {
  SORT_OPTION_LIST_WITHOUT_SCORE,
  type SortOption,
} from '../../../../entities/post/api/index';
import { InsightFilterHeader } from '../../insight-layout/ui/insight-filter.header';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';

export function FilteredPostsPage() {
  const fetchEnabled = useCurrentPageRoutingStatus({ currentPath: '/insight/filtered-posts' });
  const insightFilters = useInsightFiltersFromSearchParams();
  const { query: selectedSavedQuery } = useSelectedSavedQuery();
  const { publicFilter } = usePublicFilter(selectedSavedQuery.id);

  const [sortBy, setSortBy] = useState<SortOption>('view');
  const queryClient = useQueryClient();
  const { startDate, endDate } = useFilteredPageDateRangeValue();
  const { selectedPostIds, selectedCount, toggleSelectedPost, clearSelectedPosts } =
    useBoardsPosts();

  const queryId = selectedSavedQuery.id;
  const queryName = selectedSavedQuery.name;

  const isSample = selectedSavedQuery.type === 'public';

  const params = useMemo(
    () => ({
      queryId,
      startDate,
      endDate,
      limit: 20,
      sortBy,
      privateFilter: insightFilters,
      publicFilter,
    }),
    [queryId, startDate, endDate, sortBy, insightFilters, publicFilter],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useInfinitePosts({
    ...params,
    enabled: fetchEnabled,
  });

  // Get all posts from all pages
  const allPosts = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.posts);
  }, [data]);

  // Get total count from API response (available from the first page)
  const totalCount = data?.pages?.[0]?.total;

  return (
    <div className="flex h-full flex-col">
      <InsightFilterHeader hideDateRange />
      <FilteredPostsHeader queryName={queryName} dateRange={{ startDate, endDate }} />

      <div className="flex-1 overflow-auto">
        <ErrorBoundary
          fallback={({ error, resetError }) => (
            <ErrorFallback error={error as Error} reset={resetError} title="Failed to load posts" />
          )}
        >
          <Suspense fallback={<PageLoader />}>
            <SocialPostsGridHeader
              type="posts"
              className="pt-0"
              sortBy={sortBy}
              onSortChange={setSortBy}
              totalCount={totalCount}
              params={params}
              isSample={isSample}
              isLoading={isPending}
              sortOptionList={SORT_OPTION_LIST_WITHOUT_SCORE}
            />
            <SocialPostsGrid
              posts={allPosts}
              isLoading={isPending}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              onLoadMore={fetchNextPage}
              className="pb-24"
              selectedPostIds={selectedPostIds}
              onTogglePostSelect={toggleSelectedPost}
            />
            <BoardsSelectionActionBar
              selectedCount={selectedCount}
              onSaveSelected={async () => {
                await requestSaveToBoardsDialog({
                  postIds: selectedPostIds,
                  onSaved: async () => {
                    clearSelectedPosts();
                    await queryClient.invalidateQueries({ queryKey: ['posts'] });
                  },
                });
              }}
              onClear={clearSelectedPosts}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
}
